import { ActionIcon, Autocomplete } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useCallback, useRef, useState } from "react";

import { useInputProps } from "../hooks/useInputProps";
import type { ComboboxItem } from "@mantine/core";

interface SearchListProps {
  id: string;
  value: any;
  required?: boolean;
  onChange?: (value: string) => void;
  width?: string;
  sectionIndex?: number;
  tag?: string;
  rowIndex?: number;
  withLabel?: boolean;
  allowText?: boolean;
  colIndex?: number;
  data: ComboboxItem[];
}

interface PlaceholderConstraint {
  prefix: string;
  suffix: string;
  keyword: string;
  regex: RegExp;
}

const escapeRegex = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const SearchList = (props: SearchListProps) => {
  const { id, value, required, onChange, data, ...rest } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const placeholderConstraintRef = useRef<PlaceholderConstraint | null>(null);
  const [isRegexSet, setIsRegexSet] = useState(false);
  const { onChange: defaultOnChange, ...inputProps } = useInputProps(
    id,
    value,
    required,
  );

  const currentValue =
    typeof inputProps.value === "string"
      ? inputProps.value
      : String(inputProps.value ?? "");

  const applyPlaceholderSelection = useCallback((text: string) => {
    const placeholderRegex = /\[([^\]]+)\]/;
    const match = placeholderRegex.exec(text);

    if (!match) {
      return {
        output: text,
        selectStart: -1,
        selectEnd: -1,
        constraint: null,
      };
    }

    const output = text.replace(placeholderRegex, match[1]);
    const selectStart = match.index;
    const selectEnd = match.index + match[1].length;
    const prefix = text.slice(0, match.index);
    const suffix = text.slice(match.index + match[0].length);
    const keyword = match[1];
    const regex = new RegExp(
      `^${escapeRegex(prefix)}(.*)${escapeRegex(suffix)}$`,
    );

    return {
      output,
      selectStart,
      selectEnd,
      constraint: {
        prefix,
        suffix,
        keyword,
        regex,
      },
    };
  }, []);

  const commitValue = useCallback(
    (text: string) => {
      const { output, selectStart, selectEnd, constraint } =
        applyPlaceholderSelection(text);
      placeholderConstraintRef.current = constraint;
      setIsRegexSet(true);
      defaultOnChange(output);
      onChange?.(output);

      if (selectStart >= 0) {
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          inputRef.current?.setSelectionRange(selectStart, selectEnd);
        });
      }
    },
    [applyPlaceholderSelection, defaultOnChange, onChange],
  );

  const findMatchingItem = useCallback(
    (searchValue: string) => {
      const query = searchValue.trim().toLowerCase();
      if (!query) return null;

      const startsWithMatch = data.find(
        item =>
          item.label?.toLowerCase().startsWith(query) ??
          item.value.toLowerCase().startsWith(query),
      );
      if (startsWithMatch) return startsWithMatch;

      return (
        data.find(
          item =>
            item.label?.toLowerCase().includes(query) ??
            item.value.toLowerCase().includes(query),
        ) ?? null
      );
    },
    [data],
  );

  const handleChange = (nextValue: string) => {
    const constraint = placeholderConstraintRef.current;

    if (!constraint) {
      defaultOnChange(nextValue);
      onChange?.(nextValue);
      return;
    }

    const match = constraint.regex.exec(nextValue);
    if (!match) {
      return;
    }

    const editableText = match[1] ?? "";
    const constrainedValue = `${constraint.prefix}${editableText}${constraint.suffix}`;

    defaultOnChange(constrainedValue);
    onChange?.(constrainedValue);
  };

  const getSearchValue = useCallback((inputValue: string) => {
    const constraint = placeholderConstraintRef.current;
    if (!constraint) return inputValue;

    const match = constraint.regex.exec(inputValue);
    return match?.[1] ?? inputValue;
  }, []);

  const constrainedFilter = useCallback(
    ({ options, search }: { options: ComboboxItem[]; search: string }) => {
      const effectiveSearch = getSearchValue(search).trim().toLowerCase();
      if (!effectiveSearch) return options;

      return options.filter(item => {
        const itemValue = item.value?.toLowerCase?.() ?? "";
        const itemLabel = item.label?.toLowerCase?.() ?? "";
        return (
          itemValue.includes(effectiveSearch) ||
          itemLabel.includes(effectiveSearch)
        );
      });
    },
    [getSearchValue],
  );

  const handleOptionSubmit = (selectedValue: string) => {
    const selectedItem = data.find(
      item => item.value === selectedValue || item.label === selectedValue,
    );

    commitValue(selectedItem?.label ?? selectedValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isRegexSet && (e.key === "Enter" || e.key === "Tab")) {
      const match = findMatchingItem(currentValue);
      if (match) {
        e.preventDefault();
        commitValue(match.label ?? "");
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const constraint = placeholderConstraintRef.current;

    if (constraint) {
      const match = constraint.regex.exec(currentValue);
      const editableText = (match?.[1] ?? "").trim();

      if (!editableText) {
        const restoredValue = `${constraint.prefix}${constraint.keyword}${constraint.suffix}`;
        defaultOnChange(restoredValue);
        onChange?.(restoredValue);
      }
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const constraint = placeholderConstraintRef.current;

    if (constraint) {
      const match = constraint.regex.exec(currentValue);
      const editableText = match?.[1] ?? "";

      if (editableText === constraint.keyword) {
        const selectStart = constraint.prefix.length;
        const selectEnd = selectStart + constraint.keyword.length;

        requestAnimationFrame(() => {
          inputRef.current?.setSelectionRange(selectStart, selectEnd);
        });
      }
    }
  };

  const handleClear = () => {
    placeholderConstraintRef.current = null;
    setIsRegexSet(false);
    defaultOnChange("");
    onChange?.("");
    inputRef.current?.blur(); // Blur first to ensure clearing dropdown state
    inputRef.current?.focus(); // Focus and reopen dropdown
  };

  const clearButton = currentValue ? (
    <ActionIcon
      size="sm"
      color="red"
      aria-label="Clear selected option"
      onMouseDown={e => e.preventDefault()}
      onClick={handleClear}>
      <IconTrash size={16} />
    </ActionIcon>
  ) : null;

  return (
    <Autocomplete
      {...rest}
      {...inputProps}
      id={id}
      ref={inputRef}
      data={isRegexSet ? [] : data}
      value={currentValue}
      onChange={handleChange}
      onOptionSubmit={handleOptionSubmit}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      filter={isRegexSet ? () => [] : constrainedFilter}
      rightSection={clearButton}
      rightSectionWidth={42}
      comboboxProps={{ withinPortal: true }}
      limit={50}
      maxDropdownHeight={200}
    />
  );
};

export default SearchList;
